import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { FileText, Download, Calendar, DollarSign, Tag, Eye, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const MisGastos = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("todas");
  const [filterType, setFilterType] = useState("mes_actual");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCategorias();
  }, []);

  useEffect(() => {
    fetchGastos();
  }, [categoriaFilter, filterType, dateRange]);

  const fetchCategorias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategorias(data);
      }
    } catch (error) {
      console.error('Error fetching categorias:', error);
    }
  };

  const fetchGastos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Calculate date range based on filter
      let startDate = "";
      let endDate = "";
      
      if (filterType === "mes_actual") {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      } else if (filterType === "ultimos_30") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 30);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "ultimos_60") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 60);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "personalizado" && dateRange.from && dateRange.to) {
        startDate = dateRange.from.toISOString().split('T')[0];
        endDate = dateRange.to.toISOString().split('T')[0];
      }

      let url = `${API_URL}/api/propietarios/${user.email}/gastos?search=${searchTerm}`;
      if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      if (categoriaFilter !== "todas") {
        url += `&categoria_id=${categoriaFilter}`;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setGastos(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchGastos();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedGastos = [...gastos].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'monto') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (key === 'fecha_gasto') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setGastos(sortedGastos);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="text-blue-600 ml-1">↑</span> : 
      <span className="text-blue-600 ml-1">↓</span>;
  };

  const openDetailModal = async (gasto) => {
    setSelectedGasto(gasto);
    setShowDetailModal(true);
    setDetailLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${gasto.id}/archivos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setArchivos(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownload = async (gastoId, archivoId, nombreArchivo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${gastoId}/archivos/${archivoId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreArchivo;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al descargar el archivo');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-MX');
  };

  const totalGastos = gastos.reduce((sum, g) => sum + g.monto, 0);

  const gastosPorCategoria = gastos.reduce((acc, gasto) => {
    const categoria = gasto.categoria_nombre || 'Sin categoría';
    if (!acc[categoria]) {
      acc[categoria] = 0;
    }
    acc[categoria] += gasto.monto;
    return acc;
  }, {});

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mis Gastos</h1>
            <p className="text-gray-500 mt-1">Consulta los gastos relacionados con tus propiedades</p>
          </div>
        </div>

        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total en Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(totalGastos)}</div>
                  <DollarSign className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Gastos Registrados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{gastos.length}</div>
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Categorías</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{Object.keys(gastosPorCategoria).length}</div>
                  <Tag className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              {/* Search and Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Buscar por concepto o descripción..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas las categorías</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mes_actual">Este mes</SelectItem>
                    <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
                    <SelectItem value="ultimos_60">Últimos 60 días</SelectItem>
                    <SelectItem value="personalizado">Personalizado</SelectItem>
                  </SelectContent>
                </Select>

                {filterType === "personalizado" && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline">
                        <Calendar className="w-4 h-4 mr-2" />
                        Seleccionar fechas
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                )}
                
                <Button onClick={handleSearch}>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gastos Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Cargando...</div>
            ) : gastos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p>No hay gastos para mostrar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('fecha_gasto')}
                      >
                        <div className="flex items-center">
                          Fecha
                          <SortIcon columnKey="fecha_gasto" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('concepto')}
                      >
                        <div className="flex items-center">
                          Concepto
                          <SortIcon columnKey="concepto" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('inmueble_nombre')}
                      >
                        <div className="flex items-center">
                          Inmueble
                          <SortIcon columnKey="inmueble_nombre" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('categoria_nombre')}
                      >
                        <div className="flex items-center">
                          Categoría
                          <SortIcon columnKey="categoria_nombre" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ligado a
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('monto')}
                      >
                        <div className="flex items-center">
                          Monto
                          <SortIcon columnKey="monto" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {gastos.map((gasto) => (
                      <tr key={gasto.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {formatDate(gasto.fecha_gasto)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-900">{gasto.concepto}</p>
                            {gasto.tipo_gasto === 'recurrente' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                                Recurrente
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {gasto.inmueble_nombre || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {gasto.categoria_nombre || 'Sin categoría'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {gasto.ligado_a === 'contrato' && gasto.contrato_numero ? (
                            <span>Contrato {gasto.contrato_numero}</span>
                          ) : gasto.ligado_a === 'propietario' ? (
                            <span>Propietario</span>
                          ) : (
                            <span>Administrativo</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(gasto.monto)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailModal(gasto)}
                            title="Ver detalle"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalle del Gasto</DialogTitle>
          </DialogHeader>
          
          {selectedGasto && (
            <div className="space-y-6">
              {/* Gasto Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Fecha</p>
                  <p className="font-medium">{formatDate(selectedGasto.fecha_gasto)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Concepto</p>
                  <p className="font-medium">{selectedGasto.concepto}</p>
                </div>
                {selectedGasto.inmueble_nombre && (
                  <div>
                    <p className="text-sm text-gray-500">Inmueble</p>
                    <p className="font-medium">{selectedGasto.inmueble_nombre}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Categoría</p>
                  <p className="font-medium">{selectedGasto.categoria_nombre || 'Sin categoría'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monto</p>
                  <p className="font-medium text-lg">{formatCurrency(selectedGasto.monto)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="font-medium">{selectedGasto.tipo_gasto === 'unico' ? 'Único' : 'Recurrente'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ligado a</p>
                  <p className="font-medium">
                    {selectedGasto.ligado_a === 'contrato' && selectedGasto.contrato_numero ? (
                      `Contrato ${selectedGasto.contrato_numero}`
                    ) : selectedGasto.ligado_a === 'propietario' ? (
                      'Propietario'
                    ) : (
                      'Administrativo'
                    )}
                  </p>
                </div>
                {selectedGasto.descripcion && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Descripción</p>
                    <p className="font-medium">{selectedGasto.descripcion}</p>
                  </div>
                )}
              </div>

              {/* Archivos */}
              {detailLoading ? (
                <p className="text-gray-500 text-center py-4">Cargando archivos...</p>
              ) : archivos.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium mb-3">Archivos adjuntos</h3>
                  <div className="space-y-2">
                    {archivos.map((archivo) => (
                      <div
                        key={archivo.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{archivo.nombre_archivo}</p>
                            <p className="text-xs text-gray-500">
                              {(archivo.tamano / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(selectedGasto.id, archivo.id, archivo.nombre_archivo)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No hay archivos adjuntos</p>
              )}

              <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
                Registrado el: {new Date(selectedGasto.fecha_registro).toLocaleString('es-MX')}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowDetailModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MisGastos;
